/**
 * CSPM Extras API module - assorted CSPM-domain API objects that don't fit
 * into the canonical cspm.ts (which is already 4,800+ LOC and owns the core
 * scan/policy/findings flows). This module hosts the auxiliary CSPM surface:
 * overview/snapshot, drift detector, exception workflow, org scanner,
 * extended findings (issue notes/SLA), extended scan/resource/collector
 * endpoints, and CSPM-scoped policy management (history/versions/severity
 * overrides/bulk enable/import-export/multi-cloud coverage).
 *
 * Real per-domain split: contains the actual implementations, extracted
 * verbatim from the monolithic frontend/src/services/api.ts as part of the
 * ADR-0003 follow-up.
 *
 * Domain objects:
 *   - cspmOverviewApi      (overview/trends/snapshot/compare)
 *   - driftDetectorApi     (baselines/scan/results/ack/revert)
 *   - exceptionServiceApi  (CSPM exceptions CRUD + approve/reject/revoke)
 *   - orgScannerApi        (org-scoped scan/posture/cross-org analytics)
 *   - cspmFindingsExtApi   (issue notes/assign/reopen/trends + SLA + summary)
 *   - cspmScanExtApi       (scan status/findings/compare + resources + collectors)
 *   - cspmPolicyMgmtApi    (policy history/versions/severity-overrides + bulk + multi-cloud)
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

// -- cspmOverviewApi --
export const cspmOverviewApi = {
  getOverview: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/overview'));
  },

  getTrends: async (params?: { days?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/overview/trends', params as any));
  },

  getSnapshot: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/overview/snapshot'));
  },

  compare: async (params?: { period?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/overview/compare', params as any));
  },
};

// -- driftDetectorApi --
// Paths align with the drift-detector child routes (port 8110) reached via the
// gateway's `/api/v1/cspm/drift` → `/api/v1/drift` prefix rewrite:
//   GET/POST   /api/v1/drift/baselines[/{id}]   (named baseline CRUD)
//   POST       /api/v1/drift/scan               (full drift scan)
//   GET        /api/v1/drift/results            (drift list)
//   POST       /api/v1/drift/acknowledge/{id}   (acknowledge)
//   POST       /api/v1/drift/revert/{id}        (revert-to-baseline, tracking)
// NOTE: two gateway rewrites currently misroute — see cloud-security-platform
// gateway path_rewrites.py: `/cspm/drift/baseline`→`/drift/results` shadows
// `/cspm/drift/baselines`, and `/cspm/drift/scan`→`/cspm/posture`. Both need
// to be repointed for these calls to reach the real routes.
export const driftDetectorApi = {
  getBaselines: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/drift/baselines'));
  },

  createBaseline: async (data: { name: string; description?: string; provider?: string; account_id?: string; resource_types?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cspm/drift/baselines', data));
  },

  deleteBaseline: async (baselineId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/cspm/drift/baselines/${baselineId}`));
  },

  scan: async (data: { account_id: string; provider?: string; baseline_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cspm/drift/scan', data));
  },

  getResults: async (params?: { status?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/drift/results', params));
  },

  acknowledge: async (driftId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/drift/acknowledge/${driftId}`));
  },

  revert: async (driftId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/drift/revert/${driftId}`));
  },
};

// -- exceptionServiceApi --
export const exceptionServiceApi = {
  list: async (params?: { status?: string; page?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/exceptions', params));
  },

  get: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cspm/exceptions/${id}`));
  },

  create: async (data: { finding_id: string; reason: string; expiry_days?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cspm/exceptions', data));
  },

  approve: async (id: string, data?: { notes?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/exceptions/${id}/approve`, data));
  },

  reject: async (id: string, data?: { notes?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/exceptions/${id}/reject`, data));
  },

  revoke: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/exceptions/${id}/revoke`));
  },
};

// -- orgScannerApi --
export const orgScannerApi = {
  getOrganizations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/org/organizations'));
  },

  scan: async (data: { org_id: string }): Promise<any> => {
    // Wiz-parity: consolidated via the data-plane primitive. The gateway
    // proxies /api/v1/data-plane/refresh/account to scan-orchestrator
    // /api/v1/scans (see services/gateway/app/main.py), which kicks off
    // the full org→account scan cascade. org_id is passed through as
    // account_id; the orchestrator body shape uses account_id.
    return _unwrap(await fetchClient.post('/api/v1/data-plane/refresh/account', {
      account_id: data.org_id,
    }));
  },

  getPostureDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/org/posture-dashboard'));
  },

  getCrossOrgAnalytics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/org/cross-org-analytics'));
  },

  getComplianceReport: async (params?: { framework?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/org/compliance-report', params));
  },

  getPolicyConflicts: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/org/policy-conflicts'));
  },

  getHealth: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/org/health'));
  },

  onboard: async (data: { provider: string; org_id: string; credentials: any }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cspm/org/onboard', data));
  },
};

// -- cspmFindingsExtApi --
export const cspmFindingsExtApi = {
  // Issue notes
  getIssueNotes: (issueId: string) =>
    fetchClient.get(`/api/v1/cspm/findings/issues/${issueId}/notes`).then(_thenUnwrap),
  addIssueNote: (issueId: string, data: { content: string }) =>
    fetchClient.post(`/api/v1/cspm/findings/issues/${issueId}/notes`, data).then(_thenUnwrap),

  // Issue assign
  assignIssue: (issueId: string, data: { assignee: string }) =>
    fetchClient.post(`/api/v1/cspm/findings/issues/${issueId}/assign`, data).then(_thenUnwrap),

  // Issue reopen
  reopenIssue: (issueId: string) =>
    fetchClient.post(`/api/v1/cspm/findings/issues/${issueId}/reopen`).then(_thenUnwrap),

  // Issue trends
  getIssueTrends: (params?: { days?: number }) =>
    fetchClient.get('/api/v1/cspm/findings/issues/trends', params).then(_thenUnwrap),

  // SLA status and breached
  getSlaStatus: (params?: { severity?: string }) =>
    fetchClient.get('/api/v1/cspm/findings/sla-status', params).then(_thenUnwrap),
  getSlaBreached: (params?: { severity?: string; limit?: number }) =>
    fetchClient.get('/api/v1/cspm/findings/sla-breached', params).then(_thenUnwrap),

  // Findings summary
  getSummary: () =>
    fetchClient.get('/api/v1/cspm/findings/summary').then(_thenUnwrap),
};

// -- cspmScanExtApi --
export const cspmScanExtApi = {
  getScanStatus: (scanId: string) =>
    fetchClient.get(`/api/v1/cspm/scan/${scanId}/status`).then(_thenUnwrap),
  getScanFindings: (scanId: string, params?: { severity?: string; page?: number; page_size?: number }) =>
    fetchClient.get(`/api/v1/cspm/scan/${scanId}/findings`, params).then(_thenUnwrap),
  compareScan: (params: { scan_id_a: string; scan_id_b: string }) =>
    fetchClient.get('/api/v1/cspm/scan/compare', params).then(_thenUnwrap),

  // Resources
  getResources: (params?: { provider?: string; type?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/cspm/resources', params).then(_thenUnwrap),
  getResourceStatistics: () =>
    fetchClient.get('/api/v1/cspm/resources/statistics').then(_thenUnwrap),

  // Collectors
  getCollectors: () =>
    fetchClient.get('/api/v1/cspm/collectors').then(_thenUnwrap),
  getCollectorStatus: () =>
    fetchClient.get('/api/v1/cspm/collectors/status').then(_thenUnwrap),
};

// -- cspmPolicyMgmtApi --
export const cspmPolicyMgmtApi = {
  // Policy change history
  getHistory: (policyId: string) =>
    fetchClient.get(`/api/v1/cspm/policies/${policyId}/history`).then(_thenUnwrap),
  // Version list (CSPM-scoped)
  getVersions: (policyId: string) =>
    fetchClient.get(`/api/v1/cspm/policies/${policyId}/versions`).then(_thenUnwrap),
  // Version diff
  compareVersions: (policyId: string, data: { version_a: number; version_b: number }) =>
    fetchClient.post(`/api/v1/cspm/policies/${policyId}/versions/compare`, data).then(_thenUnwrap),
  // Rollback
  rollback: (policyId: string, data: { version: number }) =>
    fetchClient.post(`/api/v1/cspm/policies/${policyId}/rollback`, data).then(_thenUnwrap),
  // Severity overrides
  getSeverityOverrides: (policyId: string) =>
    fetchClient.get(`/api/v1/cspm/policies/${policyId}/severity-overrides`).then(_thenUnwrap),
  createSeverityOverride: (policyId: string, data: { severity: string; reason?: string; scope?: string }) =>
    fetchClient.post(`/api/v1/cspm/policies/${policyId}/severity-overrides`, data).then(_thenUnwrap),
  deleteSeverityOverride: (policyId: string, overrideId: string) =>
    fetchClient.delete(`/api/v1/cspm/policies/${policyId}/severity-overrides/${overrideId}`).then(_thenUnwrap),
  // Bulk enable
  bulkEnable: (data: { policy_ids: string[]; enabled: boolean }) =>
    fetchClient.post('/api/v1/cspm/policies/bulk-enable', data).then(_thenUnwrap),
  // Export policies
  exportPolicies: (params?: { format?: string; framework?: string }) =>
    fetchClient.get('/api/v1/cspm/policies/export', params).then(_thenUnwrap),
  // Import policies
  importPolicies: (data: { policies: any[]; overwrite?: boolean }) =>
    fetchClient.post('/api/v1/cspm/policies/import', data).then(_thenUnwrap),
  // Multi-cloud coverage matrix
  getMultiCloudCoverage: () =>
    fetchClient.get('/api/v1/cspm/policies/multi-cloud/coverage').then(_thenUnwrap),
  // Category filter dropdown
  getCategories: () =>
    fetchClient.get('/api/v1/cspm/policies/categories').then(_thenUnwrap),
};
